import { useCallback, useState } from 'react'
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline'

export interface RosterGroup {
  id: string
  name: string
  description: string | null
  color: string | null
  member_count: number
}

const GROUP_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6',
  '#8b5cf6', '#ec4899', '#6b7280',
]

interface GroupManagerProps {
  groups: RosterGroup[]
  activeGroupId: string | null
  onSelectGroup: (groupId: string | null) => void
  onCreate: (name: string, color: string) => Promise<void>
  onUpdate: (groupId: string, name: string, color: string) => Promise<void>
  onDelete: (groupId: string) => Promise<void>
}

export default function GroupManager({
  groups, activeGroupId, onSelectGroup, onCreate, onUpdate, onDelete,
}: GroupManagerProps) {
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [color, setColor] = useState(GROUP_COLORS[0])

  const handleCreate = useCallback(async () => {
    if (!name.trim()) return
    await onCreate(name.trim(), color)
    setName('')
    setColor(GROUP_COLORS[0])
    setShowCreate(false)
  }, [name, color, onCreate])

  const startEdit = (group: RosterGroup) => {
    setEditingId(group.id)
    setName(group.name)
    setColor(group.color || GROUP_COLORS[0])
  }

  const handleUpdate = useCallback(async () => {
    if (!editingId || !name.trim()) return
    await onUpdate(editingId, name.trim(), color)
    setEditingId(null)
    setName('')
  }, [editingId, name, color, onUpdate])

  const handleDelete = useCallback(async (groupId: string) => {
    if (!window.confirm('Delete this group? Athletes will be unassigned from it.')) return
    await onDelete(groupId)
    if (activeGroupId === groupId) onSelectGroup(null)
  }, [activeGroupId, onDelete, onSelectGroup])

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Groups</span>
        <button
          type="button"
          onClick={() => { setShowCreate(!showCreate); setEditingId(null); setName(''); }}
          className="rounded-lg p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
        >
          <PlusIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Group chips */}
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onSelectGroup(null)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
            !activeGroupId ? 'bg-stone-900 text-white' : 'border border-stone-200 text-stone-600 hover:border-stone-400'
          }`}
        >
          All
        </button>
        {groups.map((g) => (
          <div key={g.id} className="group relative inline-flex">
            <button
              type="button"
              onClick={() => onSelectGroup(activeGroupId === g.id ? null : g.id)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                activeGroupId === g.id ? 'text-white' : 'border border-stone-200 text-stone-600 hover:border-stone-400'
              }`}
              style={activeGroupId === g.id && g.color ? { backgroundColor: g.color } : activeGroupId === g.id ? { backgroundColor: '#1c1917' } : undefined}
            >
              {g.name}
              <span className="ml-1 opacity-60">{g.member_count}</span>
            </button>
            <div className="absolute -right-1 -top-1 hidden gap-0.5 group-hover:flex">
              <button type="button" onClick={() => startEdit(g)} className="rounded-full bg-white p-0.5 shadow-sm hover:bg-stone-100">
                <PencilIcon className="h-2.5 w-2.5 text-stone-500" />
              </button>
              <button type="button" onClick={() => handleDelete(g.id)} className="rounded-full bg-white p-0.5 shadow-sm hover:bg-red-50">
                <TrashIcon className="h-2.5 w-2.5 text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit form */}
      {(showCreate || editingId) && (
        <div className="flex items-center gap-2 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-3">
          <input
            className="flex-1 rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); editingId ? handleUpdate() : handleCreate(); } }}
            placeholder="Group name"
            autoFocus
          />
          <div className="flex gap-1">
            {GROUP_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`h-5 w-5 rounded-full transition ${color === c ? 'ring-2 ring-stone-600 ring-offset-1' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={editingId ? handleUpdate : handleCreate}
            disabled={!name.trim()}
            className="rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          >
            {editingId ? 'Update' : 'Create'}
          </button>
          <button
            type="button"
            onClick={() => { setShowCreate(false); setEditingId(null); setName(''); }}
            className="text-xs text-stone-400 hover:text-stone-600"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}
